/**
 * 3D length label overlay with dashed leader lines to both endpoints.
 *
 * Places the label on the opposite side of the canvas from the midpoint,
 * draws dashed leader lines from the label anchor to both endpoints,
 * and renders the text above a horizontal underline.
 */

import type { Vec3 } from '../../domain';
import type { IViewport } from '../../viewports';
import type { IOverlayObject } from '../types';

export interface LengthLabelOverlayConfig {
    readonly id: string;
    readonly measurementName: string;
    readonly value: number;
    readonly point1: Vec3;
    readonly point2: Vec3;
    readonly color?: string;
    readonly labelOffset?: number;
}

export class LengthLabelOverlay implements IOverlayObject {
    readonly id: string;
    readonly zIndex = 250;
    readonly interactive = false;
    readonly clipByCamera = false;
    visible = true;

    private measurementName: string;
    private value: number;
    private point1: Vec3;
    private point2: Vec3;
    private color: string;
    private labelOffset: number;

    private readonly font = "14px 'JetBrains Mono', monospace";
    private readonly lineWidth = 1;

    constructor(config: LengthLabelOverlayConfig) {
        this.id = config.id;
        this.measurementName = config.measurementName;
        this.value = config.value;
        this.point1 = config.point1;
        this.point2 = config.point2;
        this.color = config.color ?? '#66ff66';
        this.labelOffset = config.labelOffset ?? 80;
    }

    setPoints(p1: Vec3, p2: Vec3): void {
        this.point1 = p1;
        this.point2 = p2;
    }

    setValue(value: number): void {
        this.value = value;
    }

    setMeasurementName(name: string): void {
        this.measurementName = name;
    }

    hitTest(_canvasX: number, _canvasY: number): boolean {
        return false;
    }

    onCameraChanged(_viewport: IViewport): void {
        // Canvas positions are updated in render()
    }

    render(ctx: CanvasRenderingContext2D, viewport: IViewport): void {
        const overlayLayer = viewport.getOverlayLayer();
        const canvasWidth = overlayLayer.getCssWidth();

        const proj1 = viewport.worldToCanvas(this.point1);
        const proj2 = viewport.worldToCanvas(this.point2);
        if (!proj1.visible && !proj2.visible) return;

        // Midpoint in world space
        const midWorld: Vec3 = [
            (this.point1[0] + this.point2[0]) / 2,
            (this.point1[1] + this.point2[1]) / 2,
            (this.point1[2] + this.point2[2]) / 2,
        ];
        const projMid = viewport.worldToCanvas(midWorld);
        if (!projMid.visible) return;

        const midX = projMid.x;
        const midY = projMid.y;

        // Place label on opposite side from midpoint
        const labelOnRight = midX > canvasWidth / 2;

        // Format label text
        const valueText = `${this.value.toFixed(1)} mm`;
        const legend = this.measurementName ? `${this.measurementName}: ${valueText}` : valueText;

        // Measure text
        ctx.font = this.font;
        const metrics = ctx.measureText(legend);
        const textWidth = metrics.width;

        // Calculate label position (offset from midpoint)
        const labelX = labelOnRight ? midX + this.labelOffset : midX - this.labelOffset - textWidth;
        const labelY = midY;

        // Anchor point (edge of label closest to midpoint)
        const anchorX = labelOnRight ? labelX : labelX + textWidth;

        ctx.save();

        ctx.strokeStyle = this.color;
        ctx.fillStyle = this.color;
        ctx.lineWidth = this.lineWidth;

        // Dashed leader lines from anchor to both endpoints
        ctx.setLineDash([6, 4]);

        ctx.beginPath();
        ctx.moveTo(anchorX, labelY);
        ctx.lineTo(proj1.x, proj1.y);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(anchorX, labelY);
        ctx.lineTo(proj2.x, proj2.y);
        ctx.stroke();

        // Solid horizontal underline
        ctx.setLineDash([]);
        const underlineEndX = labelOnRight ? labelX + textWidth : labelX;
        ctx.beginPath();
        ctx.moveTo(anchorX, labelY);
        ctx.lineTo(underlineEndX, labelY);
        ctx.stroke();

        // Text above the underline
        ctx.textAlign = labelOnRight ? 'left' : 'right';
        ctx.textBaseline = 'bottom';
        const textX = labelOnRight ? labelX : labelX + textWidth;

        // Shadow for visibility
        ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;

        ctx.fillText(legend, textX, labelY - 2);

        ctx.restore();
    }
}
